import { useEffect, useState } from 'react';
import { Shield, Mail, MapPin, ChevronRight, Phone, Globe } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';

/**
 * Privacy Policy — public, no auth required.
 *
 * Content frozen against the version supplied by the client on 29 May 2026.
 * To update wording, edit the `SECTIONS` array below (each entry is a
 * stable id + heading + a `Body` render fn) — the sticky TOC, anchors,
 * deep-links and accessibility metadata all derive from this single
 * source so they can never drift out of sync with the headings.
 */

const LAST_UPDATED = '29 May 2026';

// External URL for the live site — used in section 1's reference list. We
// don't link off-page anywhere except the explicit ICO link in section 15.
const SITE_URL = 'https://peterboroughrepaircafe.uk/';
const CONTACT_EMAIL = 'peterborough.repair.cafe@gmail.com';

// ─── Reusable bits ────────────────────────────────────────────────────────
const P = ({ children }) => (
  <p className="text-gray-700 leading-relaxed">{children}</p>
);

const SubH = ({ children }) => (
  <h3 className="text-lg sm:text-xl font-semibold text-[#193460] mt-6 mb-3">
    {children}
  </h3>
);

const UL = ({ items }) => (
  <ul className="list-disc pl-6 space-y-1.5 text-gray-700 marker:text-[#E87B6A]">
    {items.map((it, i) => (
      <li key={i} className="leading-relaxed">{it}</li>
    ))}
  </ul>
);

const AddressBlock = ({ lines }) => (
  <address className="not-italic text-[#193460] font-medium leading-snug">
    {lines.map((l, i) => (
      <span key={i}>
        {l}
        {i < lines.length - 1 && <br />}
      </span>
    ))}
  </address>
);

/**
 * Highlighted contact card. Used for the Peterborough Repair Cafe UK
 * address/email AND for the ICO complaints address — wherever the user
 * is most likely to copy/click.
 */
const ContactCard = ({ title, lines, email, website, phone, accent = 'coral' }) => {
  const colours =
    accent === 'navy'
      ? {
          border: 'border-[#193460]',
          bg: 'bg-blue-50/40',
          dot: 'bg-[#193460]',
          dotAlt: 'bg-[#E87B6A]',
        }
      : {
          border: 'border-[#E87B6A]',
          bg: 'bg-orange-50/40',
          dot: 'bg-[#E87B6A]',
          dotAlt: 'bg-[#193460]',
        };
  return (
    <Card
      className={`border-2 ${colours.border} ${colours.bg} not-prose`}
      data-testid="privacy-contact-card"
    >
      <CardContent className="p-5 sm:p-6 space-y-4">
        {title && (
          <p className="text-xs uppercase tracking-wider text-gray-500 font-semibold">
            {title}
          </p>
        )}
        {lines && (
          <div className="flex items-start gap-3">
            <div
              className={`w-10 h-10 rounded-full ${colours.dotAlt} flex items-center justify-center flex-shrink-0`}
            >
              <MapPin className="w-5 h-5 text-white" />
            </div>
            <AddressBlock lines={lines} />
          </div>
        )}
        {email && (
          <div className="flex items-start gap-3">
            <div
              className={`w-10 h-10 rounded-full ${colours.dot} flex items-center justify-center flex-shrink-0`}
            >
              <Mail className="w-5 h-5 text-white" />
            </div>
            <div className="min-w-0">
              <p className="text-xs uppercase tracking-wider text-gray-500 font-semibold mb-0.5">
                Email
              </p>
              <a
                href={`mailto:${email}`}
                className="text-[#193460] font-medium hover:text-[#E87B6A] break-all"
                data-testid="privacy-contact-email"
              >
                {email}
              </a>
            </div>
          </div>
        )}
        {website && (
          <div className="flex items-start gap-3">
            <div
              className={`w-10 h-10 rounded-full ${colours.dotAlt} flex items-center justify-center flex-shrink-0`}
            >
              <Globe className="w-5 h-5 text-white" />
            </div>
            <div className="min-w-0">
              <p className="text-xs uppercase tracking-wider text-gray-500 font-semibold mb-0.5">
                Website
              </p>
              <a
                href={website}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#193460] font-medium hover:text-[#E87B6A] break-all"
              >
                {website}
              </a>
            </div>
          </div>
        )}
        {phone && (
          <div className="flex items-start gap-3">
            <div
              className={`w-10 h-10 rounded-full ${colours.dot} flex items-center justify-center flex-shrink-0`}
            >
              <Phone className="w-5 h-5 text-white" />
            </div>
            <div className="min-w-0">
              <p className="text-xs uppercase tracking-wider text-gray-500 font-semibold mb-0.5">
                Telephone
              </p>
              <a
                href={`tel:${phone.replace(/\s+/g, '')}`}
                className="text-[#193460] font-medium hover:text-[#E87B6A]"
              >
                {phone}
              </a>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// ─── Section content ──────────────────────────────────────────────────────
const SECTIONS = [
  {
    id: 'introduction',
    title: '1. Introduction',
    Body: () => (
      <div className="space-y-4">
        <P>
          Peterborough Repair Cafe UK is committed to protecting and
          respecting your privacy.
        </P>
        <P>
          This Privacy Policy explains how we collect, use, store, and
          protect your personal information when you visit our website,
          submit a repair booking, contact us, volunteer with us, or
          otherwise engage with our activities.
        </P>
        <P>
          We are committed to processing personal data in accordance with
          the UK General Data Protection Regulation (UK GDPR), the Data
          Protection Act 2018, and all other applicable data protection
          legislation.
        </P>
      </div>
    ),
  },
  {
    id: 'who-we-are',
    title: '2. Who We Are',
    Body: () => (
      <div className="space-y-4">
        <P>
          Peterborough Repair Cafe UK is a volunteer-led community
          initiative that promotes repair, reuse, sustainability, skills
          sharing, and community engagement.
        </P>
        <P>
          For the purposes of UK data protection legislation, Peterborough
          Repair Cafe UK acts as the Data Controller for personal
          information collected through this website.
        </P>
        <ContactCard
          title="Registered Address"
          lines={[
            'Railworld Wildlife Haven',
            'Pleasure Fair Meadow Road',
            'Peterborough',
            'PE2 9NR',
          ]}
          email={CONTACT_EMAIL}
          accent="coral"
        />
      </div>
    ),
  },
  {
    id: 'information-we-collect',
    title: '3. Information We Collect',
    Body: () => (
      <div className="space-y-4">
        <SubH>Information You Provide</SubH>
        <P>We may collect personal information including:</P>
        <UL
          items={[
            'Name',
            'Email address',
            'Telephone number',
            'Postcode',
            'Details of items submitted for repair',
            'Volunteer application information',
            'Information submitted through contact forms',
            'Any other information you choose to provide',
          ]}
        />
        <SubH>Information Collected Automatically</SubH>
        <P>When visiting our website, we may automatically collect:</P>
        <UL
          items={[
            'IP address',
            'Browser type and version',
            'Device information',
            'Pages visited',
            'Date and time of visits',
            'Website usage statistics',
          ]}
        />
        <P>
          This information helps us understand website usage, improve
          performance, and maintain security.
        </P>
      </div>
    ),
  },
  {
    id: 'how-we-use-information',
    title: '4. How We Use Your Information',
    Body: () => (
      <div className="space-y-4">
        <P>We may use personal information to:</P>
        <UL
          items={[
            'Manage repair bookings and appointments',
            'Contact you regarding repairs or enquiries',
            'Coordinate volunteers and community activities',
            'Respond to information requests',
            'Improve our services and website',
            'Monitor website performance and security',
            'Produce anonymised repair and sustainability statistics',
            'Support grant applications and reporting requirements',
            'Meet legal, insurance, safeguarding, regulatory, or administrative obligations',
          ]}
        />
        <P>
          We will only process personal information where we have a lawful
          basis for doing so.
        </P>
      </div>
    ),
  },
  {
    id: 'lawful-basis',
    title: '5. Lawful Basis for Processing',
    Body: () => (
      <div className="space-y-4">
        <P>
          Under UK GDPR, we rely on one or more of the following lawful
          bases:
        </P>
        <SubH>Legitimate Interests</SubH>
        <P>
          To operate and manage Peterborough Repair Cafe UK, organise
          repair events, communicate with participants and volunteers, and
          deliver community services.
        </P>
        <SubH>Consent</SubH>
        <P>
          Where you have provided clear consent, including subscribing to
          updates or consenting to the use of photographs.
        </P>
        <P>Consent may be withdrawn at any time.</P>
        <SubH>Legal Obligation</SubH>
        <P>
          Where processing is necessary to comply with legal, safeguarding,
          regulatory, insurance, or administrative requirements.
        </P>
      </div>
    ),
  },
  {
    id: 'repair-bookings',
    title: '6. Repair Bookings',
    Body: () => (
      <div className="space-y-4">
        <P>
          When submitting a repair booking, we collect information
          necessary to:
        </P>
        <UL
          items={[
            'Arrange and manage repair appointments',
            'Contact you regarding bookings',
            'Record repairs undertaken',
            'Monitor environmental and community impact',
          ]}
        />
        <P>Repair outcome information may be anonymised and used for:</P>
        <UL
          items={[
            'Sustainability reporting',
            'Funding applications',
            'Impact reporting',
            'Community engagement reporting',
            'Demonstrating the value and impact of Peterborough Repair Cafe UK',
          ]}
        />
      </div>
    ),
  },
  {
    id: 'photography-media',
    title: '7. Photography and Media',
    Body: () => (
      <div className="space-y-4">
        <P>
          Photographs and videos may occasionally be taken during Repair
          Cafe events for promotional, educational, reporting, publicity,
          and funding purposes.
        </P>
        <P>Media may be used on:</P>
        <UL
          items={[
            'The Peterborough Repair Cafe UK website',
            'Social media channels',
            'Printed promotional materials',
            'Funding and grant reports',
            'Local media publications',
          ]}
        />
        <P>
          Where practical, appropriate consent will be obtained when
          individuals are clearly identifiable.
        </P>
        <P>
          If you do not wish to be photographed or filmed, please inform a
          member of the Peterborough Repair Cafe UK team during the event.
        </P>
      </div>
    ),
  },
  {
    id: 'cookies',
    title: '8. Cookies',
    Body: () => (
      <div className="space-y-4">
        <P>Our website may use cookies and similar technologies to:</P>
        <UL
          items={[
            'Ensure correct website functionality',
            'Improve user experience',
            'Analyse website usage',
            'Monitor website performance',
          ]}
        />
        <P>
          You may control cookies through your browser settings. Disabling
          cookies may affect some website functionality.
        </P>
      </div>
    ),
  },
  {
    id: 'sharing-information',
    title: '9. Sharing Your Information',
    Body: () => (
      <div className="space-y-4">
        <P>We do not sell, rent, or trade personal information.</P>
        <P>Information may be shared with:</P>
        <UL
          items={[
            'Volunteers involved in organising repairs',
            'Website hosting providers',
            'Online booking and form providers',
            'Email service providers',
            'Professional advisers where necessary',
            'Regulatory, safeguarding, insurance, or legal authorities where required by law',
          ]}
        />
        <P>
          All third-party providers processing information on our behalf
          are expected to handle personal data securely and in accordance
          with applicable data protection legislation.
        </P>
      </div>
    ),
  },
  {
    id: 'data-retention',
    title: '10. Data Retention',
    Body: () => (
      <div className="space-y-4">
        <P>
          Personal information is retained only for as long as reasonably
          necessary to fulfil the purposes for which it was collected.
        </P>
        <P>This may include:</P>
        <UL
          items={[
            'Managing repair bookings and enquiries',
            'Coordinating volunteers and community activities',
            'Meeting legal, regulatory, safeguarding, insurance, or administrative requirements',
            'Maintaining records relating to the operation and impact of Peterborough Repair Cafe UK',
          ]}
        />
        <P>
          When information is no longer required, it will be securely
          deleted, anonymised, or otherwise disposed of in accordance with
          applicable legislation and good practice.
        </P>
        <P>
          Anonymous or aggregated statistical information that does not
          identify individuals may be retained indefinitely for historical,
          reporting, sustainability, funding, and research purposes.
        </P>
      </div>
    ),
  },
  {
    id: 'your-rights',
    title: '11. Your Rights',
    Body: () => (
      <div className="space-y-4">
        <P>Under UK GDPR, you have the right to:</P>
        <UL
          items={[
            'Request access to your personal information',
            'Request correction of inaccurate information',
            'Request erasure of personal information where appropriate',
            'Request restriction of processing',
            'Object to processing',
            'Request transfer of personal information where applicable',
            'Withdraw consent where consent is relied upon',
          ]}
        />
        <P>
          To exercise any of these rights, please contact us using the
          details provided below.
        </P>
      </div>
    ),
  },
  {
    id: 'data-security',
    title: '12. Data Security',
    Body: () => (
      <div className="space-y-4">
        <P>
          We take reasonable technical and organisational measures to
          protect personal information from:
        </P>
        <UL
          items={[
            'Unauthorised access',
            'Loss',
            'Misuse',
            'Disclosure',
            'Alteration',
            'Destruction',
          ]}
        />
        <P>
          While we take security seriously, no method of electronic
          transmission or storage can be guaranteed to be completely
          secure.
        </P>
      </div>
    ),
  },
  {
    id: 'childrens-privacy',
    title: "13. Children's Privacy",
    Body: () => (
      <div className="space-y-4">
        <P>
          Peterborough Repair Cafe UK welcomes participation from
          individuals of all ages.
        </P>
        <P>
          Where personal information relating to children is collected,
          appropriate parental or guardian consent will be obtained where
          required by law.
        </P>
      </div>
    ),
  },
  {
    id: 'third-party-websites',
    title: '14. Third-Party Websites',
    Body: () => (
      <div className="space-y-4">
        <P>Our website may contain links to third-party websites.</P>
        <P>
          Peterborough Repair Cafe UK is not responsible for the content,
          privacy practices, security, or policies of external websites and
          encourages users to review the privacy policies of those sites
          separately.
        </P>
      </div>
    ),
  },
  {
    id: 'complaints',
    title: '15. Complaints',
    Body: () => (
      <div className="space-y-4">
        <P>
          If you have concerns about how we process personal information,
          please contact us first so that we can attempt to resolve the
          issue.
        </P>
        <P>
          You also have the right to lodge a complaint with the Information
          Commissioner&apos;s Office (ICO).
        </P>
        <ContactCard
          title="Information Commissioner's Office (ICO)"
          lines={[
            'Wycliffe House',
            'Water Lane',
            'Wilmslow',
            'Cheshire',
            'SK9 5AF',
          ]}
          website="https://www.ico.org.uk"
          phone="0303 123 1113"
          accent="navy"
        />
      </div>
    ),
  },
  {
    id: 'contact',
    title: '16. Contact Us',
    Body: () => (
      <div className="space-y-4">
        <P>
          For any questions regarding this Privacy Policy or how personal
          information is processed, please contact:
        </P>
        <ContactCard
          title="Peterborough Repair Cafe UK"
          lines={[
            'Railworld Wildlife Haven',
            'Pleasure Fair Meadow Road',
            'Peterborough',
            'PE2 9NR',
          ]}
          email={CONTACT_EMAIL}
          website={SITE_URL}
          accent="coral"
        />
      </div>
    ),
  },
  {
    id: 'changes',
    title: '17. Changes to This Privacy Policy',
    Body: () => (
      <div className="space-y-4">
        <P>
          We may update this Privacy Policy periodically to reflect changes
          in our activities, legal obligations, website functionality, or
          operational requirements.
        </P>
        <P>
          Any updates will be published on this page and will take effect
          immediately upon publication.
        </P>
      </div>
    ),
  },
];

// ─── Page component ──────────────────────────────────────────────────────
export default function PrivacyPolicy() {
  const [activeSection, setActiveSection] = useState(SECTIONS[0]?.id);

  // SEO — set <title> + meta description, restore on unmount.
  useEffect(() => {
    const prevTitle = document.title;
    document.title = 'Privacy Policy | Peterborough Repair Cafe UK';

    const META_NAME = 'description';
    const DESCRIPTION =
      'Read the Privacy Policy for Peterborough Repair Cafe UK, including how we collect, use, store and protect personal information.';

    let metaEl = document.querySelector(`meta[name="${META_NAME}"]`);
    const created = !metaEl;
    const prevContent = metaEl?.getAttribute('content') ?? null;
    if (!metaEl) {
      metaEl = document.createElement('meta');
      metaEl.setAttribute('name', META_NAME);
      document.head.appendChild(metaEl);
    }
    metaEl.setAttribute('content', DESCRIPTION);

    return () => {
      document.title = prevTitle;
      if (created) {
        metaEl.parentNode?.removeChild(metaEl);
      } else if (prevContent !== null) {
        metaEl.setAttribute('content', prevContent);
      }
    };
  }, []);

  // Scroll to top on mount.
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, []);

  // Active-section highlight for the sticky TOC.
  useEffect(() => {
    if (typeof window === 'undefined' || !('IntersectionObserver' in window)) {
      return undefined;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible.length > 0) {
          setActiveSection(visible[0].target.id);
        }
      },
      { rootMargin: '-25% 0px -55% 0px', threshold: 0 },
    );
    SECTIONS.forEach((s) => {
      const el = document.getElementById(s.id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  const handleTocClick = (e, id) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      if (window.history?.replaceState) {
        window.history.replaceState(null, '', `#${id}`);
      }
    }
  };

  return (
    <main className="min-h-screen bg-gray-50" data-testid="privacy-policy-page">
      {/* Header / hero */}
      <section className="bg-[#193460] text-white py-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-full bg-[#E87B6A] flex items-center justify-center flex-shrink-0">
              <Shield className="w-6 h-6" />
            </div>
            <div>
              <p className="text-sm uppercase tracking-wider text-gray-300">
                Peterborough Repair Cafe UK
              </p>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight">
                Privacy Policy
              </h1>
            </div>
          </div>
          <p className="text-base sm:text-lg text-gray-200">
            <span className="font-semibold">Last Updated:</span> {LAST_UPDATED}
          </p>
        </div>
      </section>

      {/* Body */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14">
        <div className="grid lg:grid-cols-[260px_minmax(0,1fr)] gap-8 lg:gap-12">
          {/* Sticky desktop table of contents */}
          <aside className="hidden lg:block">
            <nav
              aria-label="Privacy Policy sections"
              data-testid="privacy-policy-toc"
              className="sticky top-24"
            >
              <p className="text-xs uppercase tracking-widest text-gray-500 mb-3 font-semibold">
                On this page
              </p>
              <ol className="space-y-1 text-sm">
                {SECTIONS.map((s) => {
                  const isActive = activeSection === s.id;
                  return (
                    <li key={s.id}>
                      <a
                        href={`#${s.id}`}
                        onClick={(e) => handleTocClick(e, s.id)}
                        className={`flex items-start gap-1 rounded-md px-2 py-1.5 transition-colors ${
                          isActive
                            ? 'bg-[#193460] text-white font-medium'
                            : 'text-gray-700 hover:bg-gray-100 hover:text-[#193460]'
                        }`}
                        aria-current={isActive ? 'true' : undefined}
                      >
                        <ChevronRight
                          className={`w-3.5 h-3.5 mt-0.5 flex-shrink-0 ${
                            isActive ? 'text-[#E87B6A]' : 'text-gray-400'
                          }`}
                          aria-hidden="true"
                        />
                        <span className="leading-snug">{s.title}</span>
                      </a>
                    </li>
                  );
                })}
              </ol>
            </nav>
          </aside>

          {/* Article body */}
          <article
            className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 sm:p-10 lg:p-12"
            data-testid="privacy-policy-content"
          >
            <p className="text-lg text-gray-700 leading-relaxed mb-10">
              Peterborough Repair Cafe UK is committed to protecting and
              respecting your privacy. This policy explains, in plain
              English, exactly what data we collect, why, and how we look
              after it.
            </p>

            <div className="space-y-12">
              {SECTIONS.map((section) => {
                const { id, title, Body } = section;
                return (
                  <section
                    key={id}
                    id={id}
                    className="scroll-mt-24"
                    aria-labelledby={`${id}-heading`}
                    data-testid={`privacy-section-${id}`}
                  >
                    <h2
                      id={`${id}-heading`}
                      className="text-2xl sm:text-3xl font-bold text-[#193460] mb-4 pb-2 border-b-2 border-gray-100"
                    >
                      {title}
                    </h2>
                    <Body />
                  </section>
                );
              })}
            </div>
          </article>
        </div>
      </div>
    </main>
  );
}
